import React, { useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CreditCard, Trash2, Plus, Zap, Lock } from "lucide-react";
import CardknoxPayDialog from "@/components/CardknoxPayDialog";

/**
 * Compact card-on-file panel + auto-charge toggle. Used inside both the
 * admin Customer detail page AND the customer self-service portal.
 *
 * Props:
 *  - customer: { id, card_on_file?, auto_charge_on_close? }
 *  - onChange(updatedCustomerFields): notify parent after a mutation so
 *    the parent can refresh local state without a full reload.
 *  - testIdPrefix: data-testid prefix (default "payment-method")
 *  - canToggleAutoCharge: render the auto-charge switch at all (default true)
 *  - lockAutoCharge: when true the switch is rendered read-only (disabled +
 *    a "Managed by Catskills Pool Supplies" hint). Used in the customer
 *    portal so customers can SEE whether auto-charge is on but can't toggle
 *    it themselves — only the office can change it.
 */
export default function PaymentMethodCard({ customer, onChange, testIdPrefix = "payment-method", canToggleAutoCharge = true, lockAutoCharge = false }) {
  const [addOpen, setAddOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const cof = customer?.card_on_file;
  const hasCard = !!cof?.token;

  const removeCard = async () => {
    if (!window.confirm("Remove the saved card on file?")) return;
    setRemoving(true);
    try {
      await api.delete(`/customers/${customer.id}/card-on-file`);
      toast.success("Saved card removed.");
      onChange?.({ card_on_file: null, auto_charge_on_close: false });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setRemoving(false); }
  };

  const setAutoCharge = async (value) => {
    setTogglingAuto(true);
    try {
      await api.put(`/customers/${customer.id}/auto-charge`, { auto_charge_on_close: value });
      onChange?.({ auto_charge_on_close: value });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setTogglingAuto(false); }
  };

  return (
    <div className="border border-border rounded-sm p-4 space-y-3 bg-secondary/20" data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center justify-between">
        <p className="overline flex items-center gap-2"><CreditCard className="w-4 h-4" /> Payment method</p>
      </div>

      {hasCard ? (
        <div className="flex items-center justify-between gap-3" data-testid={`${testIdPrefix}-on-file`}>
          <div className="flex items-center gap-3">
            <div className="font-mono text-sm">
              {(cof.brand || "CARD").toUpperCase()} •••• {cof.last4 || "????"}
            </div>
            <span className="text-xs text-muted-foreground mono-num">
              exp {(cof.exp || "----").slice(0,2)}/{(cof.exp || "----").slice(2,4)}
            </span>
          </div>
          <Button
            variant="outline" size="sm"
            disabled={removing}
            onClick={removeCard}
            className="rounded-sm"
            data-testid={`${testIdPrefix}-remove`}
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={() => setAddOpen(true)}
          className="rounded-sm w-full"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="w-4 h-4 mr-2" /> Add credit card
        </Button>
      )}

      {hasCard && canToggleAutoCharge && (
        <label className={`flex items-start gap-3 pt-1 ${lockAutoCharge ? "cursor-default" : "cursor-pointer"}`} data-testid={`${testIdPrefix}-auto-charge-row`}>
          <Switch
            checked={!!customer.auto_charge_on_close}
            onCheckedChange={lockAutoCharge ? undefined : setAutoCharge}
            disabled={togglingAuto || lockAutoCharge}
            data-testid={`${testIdPrefix}-auto-charge-switch`}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5 text-primary" /> Auto-charge on delivery
              {lockAutoCharge && (
                <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground font-normal">
                  <Lock className="w-3 h-3" /> Managed by office
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {lockAutoCharge
                ? "When this is on, your card on file is charged automatically each time a delivery is completed. Contact the office to turn this on or off."
                : "When a driver closes a ticket and an invoice is created, the card on file is charged automatically."}
            </p>
          </div>
        </label>
      )}

      <CardknoxPayDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        invoice={{ number: "", total: 0 }}
        customerId={customer.id}
        mode="save-only"
        onPaid={(data) => {
          onChange?.({
            card_on_file: {
              token: "***",  // we don't expose the token client-side; parent reload will get the real shape
              last4: data?.last4 || "",
              brand: data?.brand || "",
            },
          });
        }}
      />
    </div>
  );
}
